<template>
  <div class="image-generator">
    <div class="gen-header">
      <h2 class="gen-title"><i class="el-icon-picture-outline"></i> 图片设计</h2>
      <p class="gen-desc">自定义形状、尺寸、颜色与边框，生成纯色或渐变图片并下载。</p>
      <a href="#background-images-section" class="gen-jump-backgrounds" @click.prevent="scrollToBackgrounds">
        <i class="el-icon-bottom"></i> 向下：背景图片
      </a>
    </div>

    <div class="gen-body">
      <div class="gen-preview-col">
        <div class="gen-preview-wrap" title="点击复制 Base64">
          <div class="gen-preview-box">
            <div
              ref="preview"
              class="gen-preview"
              :style="previewStyleScaled"
              @click="copyBase64ToClipboard"
            ></div>
          </div>
          <p class="gen-preview-hint">点击图片复制 Base64</p>
        </div>
        <div class="gen-preview-below">
          <div class="gen-preview-actions">
            <el-button type="primary" size="small" icon="el-icon-download" @click="downloadImage">下载图片</el-button>
            <el-button size="small" icon="el-icon-document-copy" @click="copyDesignImage">复制图片</el-button>
            <el-button size="small" icon="el-icon-document-copy" @click="copyDesignImageBase64">复制 Base64</el-button>
          </div>
          <p class="gen-download-hint">下载图片、复制 Base64，支持背景透明；复制的图片部分软件中透明区域会显示为黑色；顶伯知识竞赛软件、顶伯评委评分软件、顶伯触摸屏答题软件建议复制 Base64，不失真，而且支持透明。</p>
        </div>
      </div>

      <div class="gen-options">
        <div class="option-row">
          <span class="option-label"><i class="el-icon-crop"></i> 形状</span>
          <el-radio-group v-model="form.shape" size="small">
            <el-radio-button label="square">方形</el-radio-button>
            <el-radio-button label="circle">圆形</el-radio-button>
          </el-radio-group>
        </div>
        <template v-if="form.shape === 'square'">
          <div class="option-row option-row-slider">
            <span class="option-label"><i class="el-icon-s-operation"></i> 圆角</span>
            <div class="slider-wrap">
              <el-slider v-model="form.radius" :min="0" :max="200" :show-input="true" size="small"></el-slider>
            </div>
          </div>
        </template>

        <div class="option-row option-row-slider">
          <span class="option-label"><i class="el-icon-rank"></i> 宽度</span>
          <div class="slider-wrap">
            <el-slider v-model="form.width" :min="50" :max="800" :show-input="true" size="small"></el-slider>
          </div>
        </div>
        <div class="option-row option-row-slider">
          <span class="option-label"><i class="el-icon-rank"></i> 高度</span>
          <div class="slider-wrap">
            <el-slider v-model="form.height" :min="50" :max="800" :show-input="true" size="small"></el-slider>
          </div>
        </div>

        <div class="option-row">
          <span class="option-label"><i class="el-icon-brush"></i> 类型</span>
          <el-radio-group v-model="form.colorMode" size="small">
            <el-radio-button label="solid">纯色</el-radio-button>
            <el-radio-button label="gradient">渐变</el-radio-button>
          </el-radio-group>
        </div>
        <div class="option-row option-row-presets">
          <span class="option-label"><i class="el-icon-magic-stick"></i> {{ form.colorMode === "solid" ? "纯色预设" : "渐变预设" }}</span>
          <div class="preset-list">
            <template v-if="form.colorMode === 'solid'">
              <div
                v-for="(p, i) in solidPresets"
                :key="'s-' + i"
                class="preset-item"
                :style="{ background: p.gradientCss, borderColor: p.borderColor }"
                :title="p.name"
                @click="applySolidPreset(p)"
              ></div>
            </template>
            <template v-else>
              <div
                v-for="(p, i) in gradientPresets"
                :key="'g-' + i"
                class="preset-item"
                :style="{ background: p.gradientCss, borderColor: p.borderColor }"
                :title="p.name"
                @click="applyGradientPreset(p)"
              ></div>
            </template>
          </div>
        </div>
        <template v-if="form.colorMode === 'solid'">
          <div class="option-row">
            <span class="option-label"><i class="el-icon-edit-outline"></i> 颜色</span>
            <el-color-picker v-model="form.solidColor" size="small" @active-change="val => onColorActiveChange(val, 'solidColor')"></el-color-picker>
            <el-input v-model="form.solidColor" size="small" class="input-color"></el-input>
          </div>
        </template>
        <template v-else>
          <div class="option-row">
            <span class="option-label"><i class="el-icon-sort"></i> 方向</span>
            <el-select v-model="form.gradientDirection" size="small" class="input-select">
              <el-option label="从左到右" value="to right"></el-option>
              <el-option label="从上到下" value="to bottom"></el-option>
              <el-option label="从左上到右下" value="to bottom right"></el-option>
              <el-option label="从右上到左下" value="to bottom left"></el-option>
            </el-select>
          </div>
          <div class="option-row">
            <span class="option-label"><i class="el-icon-edit"></i> 起始色</span>
            <el-color-picker v-model="form.gradientStart" size="small" @active-change="val => onColorActiveChange(val, 'gradientStart')"></el-color-picker>
            <el-input v-model="form.gradientStart" size="small" class="input-color"></el-input>
          </div>
          <div class="option-row">
            <span class="option-label"><i class="el-icon-edit"></i> 结束色</span>
            <el-color-picker v-model="form.gradientEnd" size="small" @active-change="val => onColorActiveChange(val, 'gradientEnd')"></el-color-picker>
            <el-input v-model="form.gradientEnd" size="small" class="input-color"></el-input>
          </div>
        </template>

        <div class="option-row option-row-slider">
          <span class="option-label"><i class="el-icon-view"></i> 透明度</span>
          <div class="slider-wrap">
            <el-slider v-model="form.opacity" :min="0" :max="100" :show-input="true" size="small"></el-slider>
          </div>
        </div>
        <div class="option-row">
          <span class="option-label"><i class="el-icon-minus"></i> 显示边框</span>
          <el-switch v-model="form.borderShow"></el-switch>
        </div>
        <template v-if="form.borderShow">
          <div class="option-row option-row-slider">
            <span class="option-label"><i class="el-icon-s-operation"></i> 边框粗细</span>
            <div class="slider-wrap">
              <el-slider v-model="form.borderWidth" :min="1" :max="30" :show-input="true" size="small"></el-slider>
            </div>
          </div>
          <div class="option-row">
            <span class="option-label"><i class="el-icon-edit"></i> 边框颜色</span>
            <el-color-picker v-model="form.borderColor" size="small" @active-change="val => onColorActiveChange(val, 'borderColor')"></el-color-picker>
            <el-input v-model="form.borderColor" size="small" class="input-color"></el-input>
          </div>
        </template>
      </div>
    </div>

    <div class="gen-usage">
      <h4 class="gen-usage-title">使用说明</h4>
      <p class="gen-usage-text">上方可自定义形状（方形/圆形）、尺寸、圆角、纯色或渐变、透明度与边框，设计图会实时预览。点击图片默认复制 Base64；设计图下方可「下载图片」「复制图片」「复制 Base64」。下方背景图片按类型分页展示，点击缩略图可全屏预览。</p>
    </div>

    <div id="background-images-section" class="gen-backgrounds">
      <h3 class="gen-backgrounds-title"><i class="el-icon-picture"></i> 背景图片</h3>
      <div v-loading="loadingBackgrounds" class="gen-backgrounds-body">
        <template v-if="backgroundImagesByType.length">
          <el-tabs v-model="activeBackgroundType" type="card" class="background-tabs">
            <el-tab-pane
              v-for="group in backgroundImagesByType"
              :key="group.type"
              :name="group.type"
            >
              <span slot="label"><i :class="backgroundTabIcon(group.type)"></i> {{ group.type }}（{{ group.list.length }}）</span>
              <div
                ref="backgroundScrollRef"
                class="background-tab-scroll"
                @scroll="onBackgroundScroll"
              >
                <div class="background-grid">
                  <a
                    v-for="item in displayListForType(group)"
                    :key="item.ID"
                    class="background-item"
                    :href="imageUrl(item)"
                    target="_blank"
                    rel="noopener"
                    :title="item.名称 || item.name"
                    @click.prevent="openBackgroundPreview(item)"
                  >
                    <img :src="imageUrl(item)" :alt="item.名称 || item.name" loading="lazy" @error="onBgImageError" />
                  </a>
                </div>
                <div v-if="hasMoreForType(group)" class="background-load-more-wrap">
                  <el-button size="small" type="primary" plain @click="loadMoreBackgrounds(group)">
                    加载更多（已显示 {{ displayListForType(group).length }} / {{ group.list.length }}）
                  </el-button>
                </div>
              </div>
            </el-tab-pane>
          </el-tabs>
        </template>
        <p v-else-if="!loadingBackgrounds" class="gen-backgrounds-empty">暂无背景图片</p>
      </div>
    </div>

    <div class="gen-usage">
      <h4 class="gen-usage-title">使用说明</h4>
      <p class="gen-usage-text">上方可自定义形状（方形/圆形）、尺寸、圆角、纯色或渐变、透明度与边框，设计图会实时预览。点击图片默认复制 Base64；设计图下方可「下载图片」「复制图片」「复制 Base64」。下方背景图片按类型分页展示，点击缩略图可全屏预览。</p>
    </div>

    <div v-if="previewImageUrl" class="background-preview-mask" @click.self="closeBackgroundPreview">
      <div class="background-preview-wrap">
        <p class="background-preview-tip">顶伯知识竞赛/评委评分/触摸屏答题软件建议使用「复制 Base64」，不失真且支持透明。</p>
        <div class="background-preview-img-wrap">
          <img
            ref="previewImgRef"
            class="background-preview-img"
            :src="previewImageUrl"
            :alt="previewImageName"
            @click="closeBackgroundPreview"
          />
        </div>
        <div class="background-preview-toolbar">
          <el-button size="small" icon="el-icon-document-copy" @click="copyBackgroundImage">复制图片</el-button>
          <el-button size="small" icon="el-icon-download" @click="downloadBackgroundImage">下载图片</el-button>
          <el-button size="small" icon="el-icon-document-copy" @click="copyBackgroundImageBase64">复制 Base64</el-button>
          <el-button size="small" icon="el-icon-close" @click="closeBackgroundPreview">关闭</el-button>
        </div>
        <div class="background-preview-hint-row">
          <span v-if="copyHintText" class="background-preview-copy-hint">{{ copyHintText }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { API_BASE_URL } from '../config'
const BACKGROUND_IMAGE_BASE = (API_BASE_URL || '').replace(/\/$/, '') + '/image-generator/files/'

export default {
  name: 'ImageGenerator',
  data() {
    return {
      windowWidth: typeof window !== 'undefined' ? window.innerWidth : 1024,
      backgroundImages: [],
      loadingBackgrounds: false,
      activeBackgroundType: '',
      displayLimitByType: {},
      previewImageUrl: '',
      previewImageName: '',
      copyHintText: '',
      showCopyHintTimeout: null,
      gradientPresets: [
        { name: '黄→橙 白边', gradientStart: '#FFE066', gradientEnd: '#FF9F43', gradientDirection: 'to right', borderColor: '#FFFFFF', gradientCss: 'linear-gradient(to right, #FFE066, #FF9F43)' },
        { name: '蓝→绿 白边', gradientStart: '#409EFF', gradientEnd: '#67C23A', gradientDirection: 'to right', borderColor: '#FFFFFF', gradientCss: 'linear-gradient(to right, #409EFF, #67C23A)' },
        { name: '粉→紫 白边', gradientStart: '#FFB8D0', gradientEnd: '#B388FF', gradientDirection: 'to right', borderColor: '#FFFFFF', gradientCss: 'linear-gradient(to right, #FFB8D0, #B388FF)' },
        { name: '青→蓝 白边', gradientStart: '#26C6DA', gradientEnd: '#5C6BC0', gradientDirection: 'to right', borderColor: '#FFFFFF', gradientCss: 'linear-gradient(to right, #26C6DA, #5C6BC0)' },
        { name: '橙→红 白边', gradientStart: '#FF9800', gradientEnd: '#F44336', gradientDirection: 'to right', borderColor: '#FFFFFF', gradientCss: 'linear-gradient(to right, #FF9800, #F44336)' },
        { name: '绿→青 白边', gradientStart: '#66BB6A', gradientEnd: '#26A69A', gradientDirection: 'to right', borderColor: '#FFFFFF', gradientCss: 'linear-gradient(to right, #66BB6A, #26A69A)' },
        { name: '紫→粉 灰边', gradientStart: '#7E57C2', gradientEnd: '#EC407A', gradientDirection: 'to right', borderColor: '#9E9E9E', gradientCss: 'linear-gradient(to right, #7E57C2, #EC407A)' },
        { name: '深蓝→靛 白边', gradientStart: '#1E88E5', gradientEnd: '#3949AB', gradientDirection: 'to bottom', borderColor: '#FFFFFF', gradientCss: 'linear-gradient(to bottom, #1E88E5, #3949AB)' },
        { name: '薄荷→绿 深绿边', gradientStart: '#80CBC4', gradientEnd: '#43A047', gradientDirection: 'to right', borderColor: '#2E7D32', gradientCss: 'linear-gradient(to right, #80CBC4, #43A047)' },
        { name: '金→橙 白边', gradientStart: '#FFD54F', gradientEnd: '#FF8F00', gradientDirection: 'to bottom', borderColor: '#FFFFFF', gradientCss: 'linear-gradient(to bottom, #FFD54F, #FF8F00)' },
        { name: '玫红→紫 白边', gradientStart: '#F06292', gradientEnd: '#BA68C8', gradientDirection: 'to right', borderColor: '#FFFFFF', gradientCss: 'linear-gradient(to right, #F06292, #BA68C8)' },
        { name: '海蓝→天蓝 白边', gradientStart: '#0D47A1', gradientEnd: '#42A5F5', gradientDirection: 'to bottom', borderColor: '#FFFFFF', gradientCss: 'linear-gradient(to bottom, #0D47A1, #42A5F5)' },
        { name: '草绿→黄绿 深绿边', gradientStart: '#9CCC65', gradientEnd: '#DCE775', gradientDirection: 'to bottom right', borderColor: '#558B2F', gradientCss: 'linear-gradient(to bottom right, #9CCC65, #DCE775)' },
        { name: '薄荷→青 白边', gradientStart: '#A7FFEB', gradientEnd: '#00BFA5', gradientDirection: 'to right', borderColor: '#FFFFFF', gradientCss: 'linear-gradient(to right, #A7FFEB, #00BFA5)' },
        { name: '樱粉→桃 白边', gradientStart: '#FCE4EC', gradientEnd: '#F48FB1', gradientDirection: 'to right', borderColor: '#FFFFFF', gradientCss: 'linear-gradient(to right, #FCE4EC, #F48FB1)' },
        { name: '日落 白边', gradientStart: '#FF6B6B', gradientEnd: '#FFE66D', gradientDirection: 'to right', borderColor: '#FFFFFF', gradientCss: 'linear-gradient(to right, #FF6B6B, #FFE66D)' },
        { name: '海洋 白边', gradientStart: '#667EEA', gradientEnd: '#764BA2', gradientDirection: 'to right', borderColor: '#FFFFFF', gradientCss: 'linear-gradient(to right, #667EEA, #764BA2)' },
        { name: '森林 白边', gradientStart: '#11998E', gradientEnd: '#38EF7D', gradientDirection: 'to bottom', borderColor: '#FFFFFF', gradientCss: 'linear-gradient(to bottom, #11998E, #38EF7D)' },
        { name: '葡萄 白边', gradientStart: '#4776E6', gradientEnd: '#8E54E9', gradientDirection: 'to right', borderColor: '#FFFFFF', gradientCss: 'linear-gradient(to right, #4776E6, #8E54E9)' },
        { name: '珊瑚 白边', gradientStart: '#FF7E5F', gradientEnd: '#FEB47B', gradientDirection: 'to right', borderColor: '#FFFFFF', gradientCss: 'linear-gradient(to right, #FF7E5F, #FEB47B)' }
      ],
      solidPresets: [
        { name: '浅蓝 白边', solidColor: '#B3E5FC', borderColor: '#FFFFFF', gradientCss: '#B3E5FC' },
        { name: '浅粉 灰边', solidColor: '#F8BBD9', borderColor: '#9E9E9E', gradientCss: '#F8BBD9' },
        { name: '浅黄 深黄边', solidColor: '#FFF9C4', borderColor: '#F9A825', gradientCss: '#FFF9C4' },
        { name: '浅紫 紫边', solidColor: '#E1BEE7', borderColor: '#7B1FA2', gradientCss: '#E1BEE7' },
        { name: '米白 棕边', solidColor: '#FFF8E1', borderColor: '#795548', gradientCss: '#FFF8E1' },
        { name: '浅绿 绿边', solidColor: '#C8E6C9', borderColor: '#2E7D32', gradientCss: '#C8E6C9' },
        { name: '浅橙 橙边', solidColor: '#FFE0B2', borderColor: '#E65100', gradientCss: '#FFE0B2' },
        { name: '深蓝 白边', solidColor: '#1565C0', borderColor: '#FFFFFF', gradientCss: '#1565C0' },
        { name: '深灰 白边', solidColor: '#546E7A', borderColor: '#FFFFFF', gradientCss: '#546E7A' },
        { name: '浅灰 深灰边', solidColor: '#ECEFF1', borderColor: '#455A64', gradientCss: '#ECEFF1' },
        { name: '浅青 青边', solidColor: '#B2DFDB', borderColor: '#00695C', gradientCss: '#B2DFDB' },
        { name: '浅红 红边', solidColor: '#FFCDD2', borderColor: '#C62828', gradientCss: '#FFCDD2' },
        { name: '深紫 白边', solidColor: '#4A148C', borderColor: '#FFFFFF', gradientCss: '#4A148C' },
        { name: '深绿 白边', solidColor: '#1B5E20', borderColor: '#FFFFFF', gradientCss: '#1B5E20' },
        { name: '天蓝 白边', solidColor: '#4FC3F7', borderColor: '#FFFFFF', gradientCss: '#4FC3F7' },
        { name: '玫红 白边', solidColor: '#F06292', borderColor: '#FFFFFF', gradientCss: '#F06292' },
        { name: '橙色 白边', solidColor: '#FF9800', borderColor: '#FFFFFF', gradientCss: '#FF9800' },
        { name: '草绿 白边', solidColor: '#81C784', borderColor: '#FFFFFF', gradientCss: '#81C784' },
        { name: '紫色 白边', solidColor: '#BA68C8', borderColor: '#FFFFFF', gradientCss: '#BA68C8' },
        { name: '金色 白边', solidColor: '#FFD54F', borderColor: '#FFFFFF', gradientCss: '#FFD54F' }
      ],
      form: {
        shape: 'square',
        radius: 0,
        width: 200,
        height: 200,
        colorMode: 'solid',
        solidColor: '#409EFF',
        gradientDirection: 'to right',
        gradientStart: '#409EFF',
        gradientEnd: '#67C23A',
        borderShow: true,
        borderWidth: 2,
        borderColor: '#303133',
        opacity: 100
      }
    }
  },
  mounted() {
    const onResize = () => { this.windowWidth = window.innerWidth }
    window.addEventListener('resize', onResize)
    this._resizeClean = () => window.removeEventListener('resize', onResize)
    this.fetchBackgroundImages()
  },
  beforeDestroy() {
    if (this._resizeClean) this._resizeClean()
    if (this.showCopyHintTimeout) clearTimeout(this.showCopyHintTimeout)
  },
  watch: {
    backgroundImagesByType: {
      handler(groups) {
        if (!groups || !groups.length) return
        if (!this.activeBackgroundType) this.activeBackgroundType = groups[0].type
        const initialLimit = 60
        groups.forEach((g) => {
          const type = g.type
          if (this.displayLimitByType[type] == null) this.$set(this.displayLimitByType, type, initialLimit)
        })
      },
      immediate: true
    }
  },
  computed: {
    previewBoxSize() {
      return this.windowWidth <= 768 ? 320 : 400
    },
    previewScale() {
      const { form } = this
      const box = this.previewBoxSize
      const s = Math.min(box / form.width, box / form.height, 1)
      return s
    },
    previewStyle() {
      const { form } = this
      const w = form.width
      const h = form.height
      const style = {
        width: w + 'px',
        height: h + 'px'
      }
      if (form.shape === 'circle') {
        style.borderRadius = '50%'
      } else {
        const r = Math.min(form.radius, form.width / 2, form.height / 2)
        style.borderRadius = Math.max(0, r) + 'px'
      }
      if (form.colorMode === 'solid') {
        style.backgroundColor = form.solidColor
      } else {
        const dir = form.gradientDirection
        style.background = `linear-gradient(${dir}, ${form.gradientStart}, ${form.gradientEnd})`
      }
      if (form.borderShow) {
        style.border = `${form.borderWidth}px solid ${form.borderColor}`
      } else {
        style.border = 'none'
      }
      style.opacity = (form.opacity != null ? form.opacity : 100) / 100
      return style
    },
    previewStyleScaled() {
      const s = this.previewScale
      return {
        ...this.previewStyle,
        transform: `scale(${s})`,
        transformOrigin: 'center center'
      }
    },
    backgroundImagesByType() {
      const list = this.backgroundImages || []
      const map = {}
      list.forEach((item) => {
        const type = (item.类型 != null ? item.类型 : item.type) != null ? String(item.类型 || item.type).trim() : ''
        if (!map[type]) map[type] = { type, list: [] }
        map[type].list.push(item)
      })
      return Object.keys(map)
        .sort((a, b) => (a === '' ? 1 : b === '' ? -1 : a.localeCompare(b)))
        .map((k) => map[k])
    }
  },
  methods: {
    fetchBackgroundImages() {
      this.loadingBackgrounds = true
      this.$axios
        .get('/image-generator/background-images')
        .then((res) => {
          this.backgroundImages = res.data && Array.isArray(res.data) ? res.data : []
        })
        .catch(() => {
          this.backgroundImages = []
        })
        .finally(() => {
          this.loadingBackgrounds = false
        })
    },
    getDisplayLimit(type) {
      return this.displayLimitByType[type] != null ? this.displayLimitByType[type] : 60
    },
    displayListForType(group) {
      if (!group || !group.list) return []
      const limit = this.getDisplayLimit(group.type)
      return group.list.slice(0, limit)
    },
    hasMoreForType(group) {
      if (!group || !group.list) return false
      return group.list.length > this.getDisplayLimit(group.type)
    },
    loadMoreBackgrounds(group) {
      if (!group || !group.list) return
      const type = group.type
      const limit = this.getDisplayLimit(type)
      if (limit >= group.list.length) return
      const next = Math.min(limit + 48, group.list.length)
      this.$set(this.displayLimitByType, type, next)
    },
    onBackgroundScroll(e) {
      const el = e && e.target
      if (!el) return
      const { scrollTop, clientHeight, scrollHeight } = el
      if (scrollTop + clientHeight < scrollHeight - 80) return
      const type = this.activeBackgroundType
      const group = (this.backgroundImagesByType || []).find((g) => g.type === type)
      if (!group || !group.list.length) return
      const limit = this.getDisplayLimit(type)
      if (limit >= group.list.length) return
      const next = Math.min(limit + 48, group.list.length)
      this.$set(this.displayLimitByType, type, next)
    },
    imageUrl(item) {
      const type = item && (item.类型 != null ? item.类型 : item.type)
      const name = item && (item.名称 != null ? item.名称 : item.name)
      if (name == null) return ''
      const typeStr = type != null ? String(type).trim() : ''
      const nameStr = String(name).trim()
      if (!nameStr) return ''
      const typePart = typeStr ? encodeURIComponent(typeStr) + '/' : ''
      return BACKGROUND_IMAGE_BASE + typePart + encodeURIComponent(nameStr)
    },
    backgroundTabIcon(type) {
      const t = (type || '').trim()
      if (t.indexOf('按钮') !== -1) return 'el-icon-s-grid'
      if (t.indexOf('题目') !== -1 || t.indexOf('答案') !== -1) return 'el-icon-document'
      if (t.indexOf('序号') !== -1) return 'el-icon-s-operation'
      return 'el-icon-picture'
    },
    onBgImageError(e) {
      if (e && e.target) e.target.style.display = 'none'
    },
    openBackgroundPreview(item) {
      this.previewImageUrl = this.imageUrl(item)
      this.previewImageName = item.名称 || item.name || ''
      document.addEventListener('keydown', this._previewEscHandler)
    },
    closeBackgroundPreview() {
      this.previewImageUrl = ''
      this.previewImageName = ''
      this.copyHintText = ''
      if (this.showCopyHintTimeout) clearTimeout(this.showCopyHintTimeout)
      document.removeEventListener('keydown', this._previewEscHandler)
    },
    scrollToBackgrounds() {
      const el = document.getElementById('background-images-section')
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
    _previewEscHandler(e) {
      if (e.key === 'Escape') this.closeBackgroundPreview()
    },
    showCopyHint(text) {
      this.copyHintText = text
      if (this.showCopyHintTimeout) clearTimeout(this.showCopyHintTimeout)
      this.showCopyHintTimeout = setTimeout(() => { this.copyHintText = '' }, 2500)
    },
    copyBackgroundImage() {
      const url = this.previewImageUrl
      if (!url) {
        this.showCopyHint('图片地址无效')
        this.$message.warning('图片地址无效')
        return
      }
      if (!navigator.clipboard || !navigator.clipboard.write) {
        this.showCopyHint('当前浏览器不支持复制图片，请右键另存为')
        this.$message.warning('当前浏览器不支持复制图片，请右键另存为')
        return
      }
      this.showCopyHint('正在复制…')
      this.$axios.get(url, { responseType: 'blob' })
        .then((res) => {
          const blob = res.data
          if (!blob || !blob.size) {
            this.showCopyHint('复制失败')
            this.$message.error('复制失败')
            return
          }
          if (blob.type === 'image/png') {
            return blob
          }
          return this._blobToPngBlob(blob)
        })
        .then((clipBlob) => {
          if (!clipBlob) return
          const toWrite = clipBlob.type === 'image/png' ? clipBlob : new Blob([clipBlob], { type: 'image/png' })
          return navigator.clipboard.write([new ClipboardItem({ 'image/png': toWrite })])
        })
        .then(() => {
          this.showCopyHint('已复制到剪贴板')
          this.$message.success('已复制到剪贴板')
        })
        .catch((err) => {
          this.showCopyHint('复制失败，请稍后重试或右键另存为')
          this.$message.error('复制失败：' + (err.response?.status === 404 ? '图片不存在' : (err.message || '请稍后重试')))
        })
    },
    downloadBackgroundImage() {
      const url = this.previewImageUrl
      if (!url) {
        this.$message.warning('图片地址无效')
        return
      }
      this.showCopyHint('正在准备下载…')
      this.$axios.get(url, { responseType: 'blob' })
        .then((res) => {
          const blob = res.data
          if (!blob || !blob.size) {
            this.showCopyHint('下载失败')
            this.$message.error('下载失败')
            return
          }
          return this._blobToPngBlob(blob)
        })
        .then((pngBlob) => {
          if (!pngBlob) return
          const name = (this.previewImageName || '背景图').replace(/\.[^.]+$/, '') || '背景图'
          const filename = name + '.png'
          const a = document.createElement('a')
          a.href = URL.createObjectURL(pngBlob)
          a.download = filename
          a.click()
          URL.revokeObjectURL(a.href)
          this.showCopyHint('已下载')
          this.$message.success('已下载：' + filename)
        })
        .catch((err) => {
          this.showCopyHint('下载失败')
          this.$message.error('下载失败：' + (err.message || '请稍后重试'))
        })
    },
    // 复制文本到剪贴板：优先 clipboard API，不可用时用 execCommand 兜底（兼容 HTTP 等非安全上下文）
    copyTextToClipboard(text) {
      if (!text || typeof text !== 'string') return Promise.reject(new Error('无内容'))
      if (navigator.clipboard && navigator.clipboard.writeText) {
        return navigator.clipboard.writeText(text)
      }
      return new Promise((resolve, reject) => {
        const textarea = document.createElement('textarea')
        textarea.value = text
        textarea.style.position = 'fixed'
        textarea.style.left = '-9999px'
        textarea.style.top = '0'
        textarea.setAttribute('readonly', '')
        document.body.appendChild(textarea)
        textarea.select()
        textarea.setSelectionRange(0, text.length)
        try {
          const ok = document.execCommand('copy')
          document.body.removeChild(textarea)
          if (ok) resolve()
          else reject(new Error('execCommand copy 失败'))
        } catch (e) {
          document.body.removeChild(textarea)
          reject(e)
        }
      })
    },
    copyBackgroundImageBase64() {
      const url = this.previewImageUrl
      if (!url) {
        this.showCopyHint('图片地址无效')
        this.$message.warning('图片地址无效')
        return
      }
      this.showCopyHint('正在生成 Base64…')
      this.$axios.get(url, { responseType: 'blob' })
        .then((res) => {
          const blob = res.data
          if (!blob || !blob.size) {
            this.showCopyHint('生成失败')
            this.$message.error('生成失败')
            return
          }
          return this._blobToPngDataURL(blob)
        })
        .then((dataUrl) => {
          if (!dataUrl) return
          return this.copyTextToClipboard(dataUrl)
        })
        .then(() => {
          this.showCopyHint('已复制 Base64 字符串')
          this.$message.success('已复制 Base64 字符串（含 data:image/png;base64, 前缀，可粘贴到其它应用）')
        })
        .catch((err) => {
          this.showCopyHint('复制 Base64 失败')
          this.$message.error('复制 Base64 失败：' + (err.message || '请稍后重试'))
        })
    },
    // 将图片转为 PNG blob，保留透明通道。不用 crossOrigin，避免 blob URL 污染 canvas 导致导出不透明
    _blobToPngBlob(blob) {
      return this._blobToPngCanvas(blob).then(({ canvas }) => {
        return new Promise((resolve, reject) => {
          canvas.toBlob(
            (pngBlob) => {
              if (pngBlob) resolve(pngBlob)
              else reject(new Error('toBlob failed'))
            },
            'image/png',
            1
          )
        })
      })
    },
    // 与下载同一套 canvas 绘制，直接 toDataURL 得到 base64，保证透明通道与下载的 PNG 一致
    _blobToPngDataURL(blob) {
      return this._blobToPngCanvas(blob).then(({ canvas }) => {
        const dataUrl = canvas.toDataURL('image/png')
        return dataUrl || Promise.reject(new Error('toDataURL failed'))
      })
    },
    _blobToPngCanvas(blob) {
      return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(blob)
        const img = new Image()
        img.onload = () => {
          URL.revokeObjectURL(url)
          const c = document.createElement('canvas')
          c.width = img.naturalWidth
          c.height = img.naturalHeight
          const ctx = c.getContext('2d', { alpha: true })
          if (!ctx) {
            reject(new Error('Canvas not supported'))
            return
          }
          ctx.clearRect(0, 0, c.width, c.height)
          ctx.drawImage(img, 0, 0)
          resolve({ canvas: c })
        }
        img.onerror = () => {
          URL.revokeObjectURL(url)
          reject(new Error('Image load failed'))
        }
        img.src = url
      })
    },
    applyGradientPreset(p) {
      this.form.colorMode = 'gradient'
      this.form.borderColor = p.borderColor
      this.form.borderShow = true
      this.form.gradientStart = p.gradientStart
      this.form.gradientEnd = p.gradientEnd
      this.form.gradientDirection = p.gradientDirection
    },
    applySolidPreset(p) {
      this.form.colorMode = 'solid'
      this.form.borderColor = p.borderColor
      this.form.borderShow = true
      this.form.solidColor = p.solidColor
    },
    onColorActiveChange(val, field) {
      if (val == null || val === '') return
      const hex = this.cssColorToHex(val)
      if (hex) this.$set(this.form, field, hex)
    },
    cssColorToHex(css) {
      if (!css || typeof css !== 'string') return ''
      if (/^#[0-9A-Fa-f]{3,8}$/.test(css)) return css
      const m = css.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*[\d.]+\s*)?\)$/)
      if (m) {
        const r = ('0' + parseInt(m[1], 10).toString(16)).slice(-2)
        const g = ('0' + parseInt(m[2], 10).toString(16)).slice(-2)
        const b = ('0' + parseInt(m[3], 10).toString(16)).slice(-2)
        return '#' + r + g + b
      }
      return ''
    },
    getCanvasBlob() {
      const canvas = document.createElement('canvas')
      const { form } = this
      const w = form.width
      const h = form.height
      canvas.width = w
      canvas.height = h
      const ctx = canvas.getContext('2d', { alpha: true })
      ctx.clearRect(0, 0, w, h)
      const opacity = form.opacity != null ? form.opacity / 100 : 1
      ctx.globalAlpha = opacity

      if (form.colorMode === 'solid') {
        ctx.fillStyle = form.solidColor
      } else {
        const [x0, y0, x1, y1] = this.gradientPoints()
        const grd = ctx.createLinearGradient(x0, y0, x1, y1)
        grd.addColorStop(0, form.gradientStart)
        grd.addColorStop(1, form.gradientEnd)
        ctx.fillStyle = grd
      }

      this.drawPath(ctx, w, h)
      ctx.fill()
      if (form.borderShow) {
        ctx.strokeStyle = form.borderColor
        ctx.lineWidth = form.borderWidth
        ctx.save()
        ctx.clip()
        ctx.stroke()
        ctx.restore()
      }

      return canvas
    },
    copyBase64ToClipboard() {
      const canvas = this.getCanvasBlob()
      const dataUrl = canvas.toDataURL('image/png')
      if (!dataUrl) {
        this.$message.warning('生成图片失败')
        return
      }
      this.copyTextToClipboard(dataUrl)
        .then(() => this.$message.success('已复制 Base64 到剪贴板'))
        .catch(() => this.$message.warning('复制失败'))
    },
    copyDesignImageBase64() {
      this.copyBase64ToClipboard()
    },
    copyDesignImage() {
      const canvas = this.getCanvasBlob()
      canvas.toBlob((blob) => {
        if (!blob) {
          this.$message.warning('生成图片失败')
          return
        }
        if (navigator.clipboard && navigator.clipboard.write) {
          navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })])
            .then(() => this.$message.success('已复制图片到剪贴板（PNG）'))
            .catch(() => this.$message.warning('复制失败，请使用「下载图片」'))
        } else {
          this.$message.warning('当前浏览器不支持复制图片，请使用「下载图片」')
        }
      }, 'image/png')
    },
    downloadImage() {
      const canvas = this.getCanvasBlob()
      const { form } = this
      const w = form.width
      const h = form.height
      const link = document.createElement('a')
      link.download = `generated-${form.shape}-${w}x${h}.png`
      link.href = canvas.toDataURL('image/png')
      link.click()
      this.$message.success('图片已下载')
    },
    gradientPoints() {
      const { form } = this
      const w = form.width
      const h = form.height
      switch (form.gradientDirection) {
        case 'to right': return [0, 0, w, 0]
        case 'to bottom': return [0, 0, 0, h]
        case 'to bottom right': return [0, 0, w, h]
        case 'to bottom left': return [w, 0, 0, h]
        default: return [0, 0, w, 0]
      }
    },
    drawPath(ctx, w, h) {
      const { form } = this
      if (form.shape === 'circle') {
        const border = form.borderShow ? form.borderWidth : 0
        const r = (Math.min(w, h) - border) / 2
        const cx = w / 2
        const cy = h / 2
        ctx.beginPath()
        ctx.arc(cx, cy, r, 0, Math.PI * 2)
        ctx.closePath()
        return
      }
      const border = form.borderShow ? form.borderWidth : 0
      const half = border / 2
      const x0 = half
      const y0 = half
      const x1 = w - half
      const y1 = h - half
      const rectW = x1 - x0
      const rectH = y1 - y0
      // 与 CSS 一致：圆角不超过宽高的一半，否则会变成胶囊形
      const r = Math.max(0, Math.min(form.radius - half, rectW / 2, rectH / 2))
      const tl = r
      const tr = r
      const br = r
      const bl = r
      ctx.beginPath()
      ctx.moveTo(x0 + tl, y0)
      ctx.lineTo(x1 - tr, y0)
      if (tr > 0) ctx.quadraticCurveTo(x1, y0, x1, y0 + tr)
      ctx.lineTo(x1, y1 - br)
      if (br > 0) ctx.quadraticCurveTo(x1, y1, x1 - br, y1)
      ctx.lineTo(x0 + bl, y1)
      if (bl > 0) ctx.quadraticCurveTo(x0, y1, x0, y1 - bl)
      ctx.lineTo(x0, y0 + tl)
      if (tl > 0) ctx.quadraticCurveTo(x0, y0, x0 + tl, y0)
      ctx.closePath()
    }
  }
}
</script>

<style lang="less" scoped>
.image-generator {
  padding: 20px 0;
  max-width: 100%;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
}
.gen-header {
  margin-bottom: 16px;
  text-align: center;
}
.gen-title {
  font-size: 20px;
  font-weight: 600;
  color: #303133;
  margin: 0 0 6px 0;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.gen-title i { color: #409EFF; }
.gen-desc {
  font-size: 13px;
  color: #606266;
  margin: 0;
  line-height: 1.5;
}
.gen-jump-backgrounds {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #409EFF;
  margin-top: 8px;
  text-decoration: none;
}
.gen-jump-backgrounds:hover { color: #66b1ff; }
.gen-body {
  display: flex;
  flex-wrap: nowrap;
  gap: 24px;
  align-items: flex-start;
  justify-content: center;
}
.gen-preview-col {
  flex-shrink: 0;
  width: 432px;
  display: flex;
  flex-direction: column;
  gap: 0;
}
.gen-preview-wrap {
  flex-shrink: 0;
  width: 100%;
  padding: 16px;
  background: #f5f7fa;
  border-radius: 12px;
  border: 1px solid #e4e7ed;
  box-shadow: 0 2px 8px rgba(0,0,0,.06);
  cursor: pointer;
  transition: box-shadow 0.2s;
  box-sizing: border-box;
}
.gen-preview-below {
  margin-top: 12px;
  padding: 0 4px;
  width: 100%;
  box-sizing: border-box;
}
.gen-preview-wrap:hover {
  box-shadow: 0 4px 12px rgba(0,0,0,.1);
}
.gen-preview-box {
  width: 400px;
  height: 400px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.gen-preview {
  display: block;
  box-sizing: border-box;
  flex-shrink: 0;
  cursor: pointer;
}
.gen-preview-hint {
  margin: 10px 0 0 0;
  font-size: 12px;
  color: #909399;
  text-align: center;
}
.gen-preview-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.gen-options {
  flex: 0 0 560px;
  min-width: 480px;
  padding: 16px 18px;
  background: #fafbfc;
  border-radius: 10px;
  border: 1px solid #e4e7ed;
  padding-left: 18px;
}
.option-row {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  flex-wrap: wrap;
  gap: 8px;
}
.option-label {
  width: 88px;
  font-size: 13px;
  color: #606266;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 4px;
}
.option-label i {
  color: #409EFF;
  font-size: 14px;
}
.option-unit {
  font-size: 12px;
  color: #909399;
}
.option-row-presets .preset-list {
  display: grid;
  grid-template-columns: repeat(9, 36px);
  gap: 8px;
  flex: 1;
  min-width: 0;
  width: fit-content;
}
.preset-item {
  width: 36px;
  height: 28px;
  border-radius: 6px;
  border: 2px solid;
  cursor: pointer;
  flex-shrink: 0;
  transition: transform 0.15s, box-shadow 0.15s;
}
.preset-item:hover {
  transform: scale(1.08);
  box-shadow: 0 2px 8px rgba(0,0,0,.2);
}
.option-row-slider .slider-wrap {
  flex: 1;
  min-width: 380px;
  padding-right: 12px;
}
.input-color { width: 96px !important; margin-left: 6px !important; }
.input-select { width: 140px !important; }
.gen-actions {
  margin-top: 14px;
}
.gen-download-hint {
  margin: 10px 0 0 0;
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
  width: 100%;
  max-width: 100%;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

#background-images-section {
  scroll-margin-top: 16px;
}
.gen-backgrounds {
  margin-top: 28px;
  padding-top: 20px;
  border-top: 1px solid #e4e7ed;
}
.gen-backgrounds-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  gap: 8px;
}
.gen-backgrounds-title i {
  color: #409EFF;
}
.gen-backgrounds-body {
  min-height: 80px;
}
.gen-backgrounds-empty {
  color: #909399;
  font-size: 13px;
  margin: 0;
}
.gen-usage {
  margin-top: 24px;
  padding: 14px 16px;
  background: #f5f7fa;
  border-radius: 8px;
  border: 1px solid #e4e7ed;
}
.gen-usage-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  margin: 0 0 8px 0;
}
.gen-usage-text {
  font-size: 13px;
  color: #606266;
  line-height: 1.6;
  margin: 0;
}
.background-tabs {
  margin-top: 0;
}
.background-tabs ::v-deep .el-tabs__header { margin-bottom: 12px; }
.background-tabs ::v-deep .el-tabs__item { font-size: 14px; }
.background-tabs ::v-deep .el-tabs__nav-wrap::after { height: 0; }
.background-tab-scroll {
  max-height: 580px;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 4px 0;
}
.background-load-more-wrap {
  text-align: center;
  margin: 16px 0;
}
.background-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 12px;
}
.background-item {
  display: block;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e4e7ed;
  background: #f5f7fa;
  transition: box-shadow 0.2s;
  height: 100px;
  flex-shrink: 0;
}
.background-item:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}
.background-item img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

.background-preview-mask {
  position: fixed;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  box-sizing: border-box;
}
.background-preview-tip {
  margin: 0 0 12px 0;
  padding: 10px 14px;
  background: linear-gradient(135deg, #ecf5ff 0%, #d9ecff 100%);
  border: 1px solid #409EFF;
  border-radius: 8px;
  color: #303133;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.5;
  text-align: center;
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.2);
}
.background-preview-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 100%;
  max-height: 100%;
}
.background-preview-img-wrap {
  background: #fff;
  border-radius: 8px;
  padding: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
}
.background-preview-img {
  max-width: 90vw;
  max-height: 75vh;
  object-fit: contain;
  display: block;
  border-radius: 4px;
  cursor: pointer;
}
.background-preview-toolbar {
  margin-top: 16px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}
.background-preview-toolbar .el-button {
  background: rgba(255, 255, 255, 0.95);
  color: #303133;
}
.background-preview-toolbar .el-button:hover {
  background: #fff;
}
.background-preview-hint-row {
  min-height: 24px;
  margin-top: 8px;
  text-align: center;
}
.background-preview-copy-hint {
  color: #67c23a;
  font-size: 14px;
}

@media (max-width: 900px) {
  .gen-body { flex-wrap: wrap; justify-content: center; }
  .gen-preview-col { width: 100%; max-width: 432px; }
  .gen-preview-wrap { width: 100%; max-width: 432px; }
}
@media (max-width: 768px) {
  .image-generator { padding: 12px 10px; }
  .gen-header { margin-bottom: 12px; }
  .gen-title { font-size: 18px; }
  .gen-body { flex-direction: column; align-items: center; flex-wrap: wrap; }
  .gen-preview-col { width: 100%; max-width: 100%; }
  .gen-preview-wrap { width: 100%; max-width: 100%; padding: 12px; }
  .gen-preview-box { width: 100%; max-width: 320px; height: 320px; margin: 0 auto; }
  .gen-preview-actions { gap: 6px; }
  .gen-preview-actions .el-button { margin: 0; }
  .gen-options { flex: 1 1 100%; min-width: 0; padding: 12px 10px; width: 100%; box-sizing: border-box; }
  .option-row-slider .slider-wrap { min-width: 140px; max-width: 100%; }
  .option-row-presets .preset-list { grid-template-columns: repeat(6, 36px); }
  .background-tabs ::v-deep .el-tabs__header {
    overflow: visible;
  }
  .background-tabs ::v-deep .el-tabs__nav-wrap {
    overflow: visible;
  }
  .background-tabs ::v-deep .el-tabs__nav-scroll {
    overflow: visible;
  }
  .background-tabs ::v-deep .el-tabs__nav {
    float: none;
    display: flex;
    flex-wrap: wrap;
    width: 100%;
  }
  .background-tabs ::v-deep .el-tabs__item {
    flex: 1 1 auto;
    white-space: nowrap;
    padding: 0 10px;
  }
  .background-grid { grid-template-columns: repeat(auto-fill, minmax(100px, 1fr)); gap: 8px; }
  .background-item { height: 88px; }
  .background-tab-scroll { max-height: 50vh; }
}
@media (max-width: 480px) {
  .image-generator { padding: 10px 8px; }
  .gen-title { font-size: 16px; }
  .gen-preview-box { max-width: 280px; height: 280px; }
  .gen-options { padding: 10px 8px; min-width: 0; }
  .option-label { width: 72px; font-size: 12px; }
  .option-row-presets .preset-list { grid-template-columns: repeat(5, 34px); gap: 6px; }
  .preset-item { width: 34px; height: 26px; }
  .gen-preview-actions { flex-direction: column; }
  .gen-preview-actions .el-button { width: 100%; }
  .background-grid { grid-template-columns: repeat(2, 1fr); gap: 6px; }
  .background-item { height: 80px; }
}
</style>

<style lang="less">
.dark-mode .image-generator {
  .gen-title { color: var(--text-primary); }
  .gen-desc { color: var(--text-regular); }
  .gen-preview-wrap {
    background: var(--bg-secondary);
    border-color: var(--border-color);
  }
  .gen-options {
    padding-left: 18px;
    background: var(--bg-secondary);
    border-color: var(--border-color);
  }
  .gen-preview-hint { color: var(--text-secondary); }
  .option-label { color: var(--text-regular); }
  .option-label i { color: #409eff; }
  .option-unit { color: var(--text-secondary); }
  .gen-backgrounds { border-top-color: var(--border-color); }
  .gen-backgrounds-title { color: var(--text-primary); }
  .gen-backgrounds-empty { color: var(--text-secondary); }
  .gen-usage { background: var(--bg-secondary); border-color: var(--border-color); }
  .gen-usage-title { color: var(--text-primary); }
  .gen-usage-text { color: var(--text-regular); }
  .gen-download-hint { color: var(--text-secondary); }
  .background-item { border-color: var(--border-color); background: var(--bg-secondary); }
  .background-preview-tip {
    background: rgba(64, 158, 255, 0.25);
    border-color: #409eff;
    color: var(--text-primary);
  }
  .background-preview-img-wrap { background: #1e1e1e; }
}
</style>
